import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, Send, Clock, CheckCircle, XCircle, FileText, Filter, Upload, X, Image, Video, File, LayoutGrid, List, Columns, Search } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useConfirm } from '../hooks/useConfirm';
import { templatesMetaAPI, instancesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PageSkeleton, SkeletonKanban } from '../components/Skeleton';

const Templates = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const showConfirm = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filtroInstancia, setFiltroInstancia] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    mensagem: '',
    categoria: 'MARKETING',
    instancia_id: '',
    media_type: '',
    media_url: '',
    buttons: [],
  });
  const [mediaFile, setMediaFile] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyOrigem, setCopyOrigem] = useState('');
  const [copyDestinos, setCopyDestinos] = useState([]);
  const [copying, setCopying] = useState(false);
  const [copyTemplateIds, setCopyTemplateIds] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('templates_viewMode') || 'kanban');

  useEffect(() => {
    loadTemplates();
    if (user?.cliente_id) loadInstances();
  }, [user?.cliente_id]);

  const loadTemplates = async () => {
    try {
      const response = await templatesMetaAPI.list();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const loadInstances = async () => {
    try {
      const response = await instancesAPI.list(user.cliente_id);
      setInstances(Array.isArray(response.data) ? response.data.filter(i => i.status === 'conectada' && i.platform === 'whatsapp' && i.provider === 'meta-api') : []);
    } catch (error) {
      console.error('Erro ao carregar instâncias:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Upload de mídia se houver
      let mediaUrl = formData.media_url;
      if (mediaFile) {
        setUploadingMedia(true);
        const formDataUpload = new FormData();
        formDataUpload.append('file', mediaFile);
        const uploadRes = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/media/${user?.id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formDataUpload,
        });
        const uploadData = await uploadRes.json();
        mediaUrl = uploadData.url;
        setUploadingMedia(false);
      }

      const payload = { ...formData, media_url: mediaUrl };
      if (!payload.media_type) delete payload.media_type;
      if (!payload.media_url) delete payload.media_url;
      if (!payload.buttons?.length) delete payload.buttons;
      
      if (editingTemplate) {
        await templatesMetaAPI.update(editingTemplate.id, payload);
        toast.success('Template atualizado!');
      } else {
        await templatesMetaAPI.create(payload);
        toast.success('Template criado!');
      }
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ nome: '', mensagem: '', categoria: 'MARKETING', instancia_id: '', media_type: '', media_url: '', buttons: [] });
      setMediaFile(null);
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar template');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleDelete = async (id) => {
    showConfirm({
      title: 'Excluir Template',
      message: 'Deseja excluir este template?',
      confirmText: 'Excluir',
      type: 'danger',
      onConfirm: async () => {
        try {
          await templatesMetaAPI.delete(id);
          toast.success('Template excluído!');
          loadTemplates();
        } catch (error) {
          toast.error('Erro ao excluir template');
        }
      }
    });
  };

  const [showSubmitModal, setShowSubmitModal] = useState(null); // template id
  const [selectedInstancias, setSelectedInstancias] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmeter = async (id) => {
    // Se só tem 1 instância, submete direto
    if (instances.length === 1) {
      try {
        await templatesMetaAPI.submeter(id, [instances[0].id]);
        toast.success('Template submetido para aprovação!');
        loadTemplates();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao submeter');
      }
    } else {
      // Abre modal para selecionar instâncias
      setSelectedInstancias(instances.map(i => i.id));
      setShowSubmitModal(id);
    }
  };

  const handleSubmeterMulti = async () => {
    if (!selectedInstancias.length) return toast.error('Selecione ao menos uma instância');
    setSubmitting(true);
    try {
      const res = await templatesMetaAPI.submeter(showSubmitModal, selectedInstancias);
      const results = res.data?.results || [];
      const ok = results.filter(r => r.status === 'submitted' || r.status === 'already_approved').length;
      const erros = results.filter(r => r.status === 'error');
      if (ok > 0) toast.success(`Template submetido em ${ok} instância(s)!`);
      if (erros.length > 0) toast.warn(`${erros.length} erro(s): ${erros[0]?.error}`);
      setShowSubmitModal(null);
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao submeter');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerificarStatus = async (id) => {
    try {
      const response = await templatesMetaAPI.verificarStatus(id);
      const status = response.data.status;
      
      if (status === 'aprovado') {
        toast.success('✅ Template aprovado!');
      } else if (status === 'rejeitado') {
        toast.error(`❌ Template rejeitado: ${response.data.rejection_reason}`);
      } else {
        toast.info('⏳ Template ainda em análise');
      }
      
      loadTemplates();
    } catch (error) {
      toast.error('Erro ao verificar status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      rascunho: { text: 'Rascunho', color: 'bg-gray-500', icon: FileText },
      submetido: { text: 'Aguardando', color: 'bg-yellow-500', icon: Clock },
      aprovado: { text: 'Aprovado', color: 'bg-green-500', icon: CheckCircle },
      rejeitado: { text: 'Rejeitado', color: 'bg-red-500', icon: XCircle },
    };
    return badges[status] || badges.rascunho;
  };

  // IDs de instâncias ativas — usado em todo o componente
  const activeInstanceIds = new Set(instances.map(i => i.id));

  if (loading) {
    return (
      <Layout expanded>
        <PageSkeleton title="Templates WhatsApp" subtitle="Gerencie templates reutilizáveis para suas campanhas">
          <SkeletonKanban />
        </PageSkeleton>
      </Layout>
    );
  }


  return (
    <Layout expanded>
      <div className="space-y-6">
        <PageHeader title="Templates WhatsApp" subtitle="Gerencie templates reutilizáveis para suas campanhas">
          <button
            onClick={async () => {
              const whatsInstances = instances.filter(i => i.platform === 'whatsapp' && i.phone_number_id);
              if (!whatsInstances.length) return toast.error('Nenhuma instância WhatsApp conectada');
              try {
                let total = 0;
                for (const inst of whatsInstances) {
                  const res = await templatesMetaAPI.sync(inst.id);
                  total += (res.data.synced || 0) + (res.data.updated || 0);
                }
                toast.success(`${total} templates sincronizados`);
                loadTemplates();
              } catch (e) { toast.error('Erro ao sincronizar'); }
            }}
            className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium border ${darkMode ? 'border-zinc-600 text-gray-300 hover:bg-zinc-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <Upload className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">Sincronizar da Meta</span>
          </button>
          {instances.length > 1 && (
            <button
              onClick={() => setShowCopyModal(true)}
              className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium border ${darkMode ? 'border-zinc-600 text-gray-300 hover:bg-zinc-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              <Send className="h-5 w-5 sm:mr-2" />
              <span className="hidden sm:inline">Copiar p/ Instância</span>
            </button>
          )}
          <button
            onClick={() => {
              setEditingTemplate(null);
              setFormData({ nome: '', mensagem: '', categoria: 'MARKETING', instancia_id: '', media_type: '', media_url: '', buttons: [] });
              setMediaFile(null);
              setShowModal(true);
            }}
            className="inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg font-medium"
          >
            <Plus className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">Novo Template</span>
          </button>
        </PageHeader>

        {/* Filtros + View Switcher */}
        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-wrap items-center gap-3">
            <Filter className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            
            {/* Busca por nome */}
            <div className="relative flex-[2] min-w-[250px]">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Buscar template..."
                value={filtroBusca}
                onChange={(e) => setFiltroBusca(e.target.value)}
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              />
            </div>

            {/* Instância */}
            <select
              value={filtroInstancia}
              onChange={(e) => setFiltroInstancia(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">Todas instâncias</option>
              {instances.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.nome_instancia}
                </option>
              ))}
            </select>

            {/* Status */}
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">Todos status</option>
              <option value="rascunho">Rascunho</option>
              <option value="submetido">Aguardando</option>
              <option value="aprovado">Aprovado</option>
              <option value="rejeitado">Rejeitado</option>
            </select>

            {/* Categoria */}
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="">Todas categorias</option>
              <option value="MARKETING">Marketing</option>
              <option value="UTILITY">Utility</option>
              <option value="AUTHENTICATION">Authentication</option>
            </select>

            {/* Mostrar instâncias inativas */}
            <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={mostrarInativas}
                onChange={(e) => setMostrarInativas(e.target.checked)}
                className="w-4 h-4 rounded text-nomoz-orange focus:ring-nomoz-orange"
              />
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Inativas</span>
            </label>

            {/* Buscar */}
            <button onClick={loadTemplates}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-nomoz-orange text-white hover:bg-nomoz-orange-dark whitespace-nowrap flex-shrink-0"
            >
              Buscar
            </button>

            {/* View Switcher */}
            <div className={`ml-auto flex rounded-lg border flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {[
                { key: 'kanban', icon: Columns, title: 'Kanban' },
                { key: 'grid', icon: LayoutGrid, title: 'Grid' },
                { key: 'table', icon: List, title: 'Tabela' },
              ].map(({ key, icon: Icon, title }, i) => (
                <button key={key}
                  onClick={() => { setViewMode(key); localStorage.setItem('templates_viewMode', key); }}
                  className={`p-2 transition ${i === 0 ? 'rounded-l-lg' : i === 2 ? 'rounded-r-lg' : ''} ${
                    viewMode === key
                      ? 'bg-nomoz-navy text-white'
                      : darkMode ? 'text-gray-400 hover:bg-zinc-700' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  title={title}>
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Views */}
        {(() => {
          // Helper: filtrar instâncias do template para mostrar só ativas
          const getActiveInstancias = (template) => {
            const all = template.instancias?.length > 0
              ? template.instancias
              : template.instancia_id ? [{ instancia_id: template.instancia_id, instancia: template.instancia, status: template.status }] : [];
            return all.filter(ti => activeInstanceIds.has(ti.instancia_id || ti.instancia?.id));
          };

          const filtered = templates
            .filter(t => {
              // Filtro instância
              if (filtroInstancia) {
                if (t.instancia_id !== filtroInstancia && !t.instancias?.some(ti => ti.instancia_id === filtroInstancia)) return false;
              } else if (!mostrarInativas) {
                // Sem filtro + sem mostrar inativas: só templates de instâncias ativas
                if (!activeInstanceIds.has(t.instancia_id) && !t.instancias?.some(ti => activeInstanceIds.has(ti.instancia_id))) return false;
              }

              // Filtro status
              if (filtroStatus && t.status !== filtroStatus) return false;

              // Filtro categoria
              if (filtroCategoria && t.categoria !== filtroCategoria) return false;

              // Filtro busca
              if (filtroBusca) {
                const busca = filtroBusca.toLowerCase();
                if (!t.nome?.toLowerCase().includes(busca) && !t.mensagem?.toLowerCase().includes(busca)) return false;
              }

              return true;
            })
            .map(t => {
              // Quando filtro por instância, usar o status DAQUELA instância
              if (filtroInstancia && t.instancias?.length) {
                const ti = t.instancias.find(ti => ti.instancia_id === filtroInstancia);
                if (ti) return { ...t, _statusFiltrado: ti.status };
              }
              return { ...t, _statusFiltrado: t.status };
            });

          if (filtered.length === 0) return (
            <div className="text-center py-12">
              <FileText className={`h-12 w-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nenhum template encontrado</p>
            </div>
          );

          // === KANBAN ===
          if (viewMode === 'kanban') {
            const cols = [
              { status: 'rascunho', title: 'Rascunho', subtitle: 'Aguardando submissão', color: 'gray' },
              { status: 'submetido', title: 'Aguardando', subtitle: 'Em análise pela Meta', color: 'yellow' },
              { status: 'aprovado', title: 'Aprovados', subtitle: 'Prontos para uso', color: 'green' },
              { status: 'rejeitado', title: 'Rejeitados', subtitle: 'Necessitam correção', color: 'red' },
            ];
            const colColors = {
              gray: { header: 'border-gray-500', count: 'bg-gray-100 text-gray-700', countDark: 'bg-gray-700 text-gray-300' },
              yellow: { header: 'border-yellow-500', count: 'bg-yellow-100 text-yellow-700', countDark: 'bg-yellow-900/20 text-yellow-400' },
              green: { header: 'border-nomoz-navy', count: 'bg-green-100 text-green-700', countDark: 'bg-green-900/20 text-green-400' },
              red: { header: 'border-red-500', count: 'bg-red-100 text-red-700', countDark: 'bg-red-900/20 text-red-400' },
            };
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cols.map(col => {
                  const items = filtered.filter(t => t._statusFiltrado === col.status);
                  const cc = colColors[col.color];
                  return (
                    <div key={col.status} className={`flex flex-col rounded-xl border-t-4 ${cc.header} ${darkMode ? 'bg-zinc-800/50 border-x border-b border-gray-700' : 'bg-gray-50 border-x border-b border-gray-200'}`}>
                      <div className="p-4 pb-3">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{col.title}</h3>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${darkMode ? cc.countDark : cc.count}`}>{items.length}</span>
                        </div>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{col.subtitle}</p>
                      </div>
                      <div className="flex-1 px-3 pb-3 space-y-3">
                        {items.length === 0 ? (
                          <div className={`text-center py-8 text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Nenhum template</div>
                        ) : items.map(template => (
                          <div key={template.id} onClick={() => setSelectedTemplate(template)}
                            className={`rounded-lg border p-3 cursor-pointer transition-all hover:shadow-md ${darkMode ? 'bg-zinc-800 border-gray-700 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-gray-400'}`}>
                            <div className={`rounded-lg p-1.5 mb-2 ${darkMode ? 'bg-zinc-900' : 'bg-[#e5ddd5]'}`}>
                              <div className="bg-[#DCF8C6] text-gray-900 rounded-lg shadow-sm overflow-hidden">
                                {template.media_url && template.media_type === 'IMAGE' && (
                                  <img src={template.media_url} alt={template.nome} className="w-full h-20 object-cover" />
                                )}
                                <div className="p-1.5">
                                  <p className="text-[11px] line-clamp-2 break-words">{template.mensagem}</p>
                                  <div className="text-[9px] text-gray-500 text-right">{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                              </div>
                            </div>
                            <h4 className={`font-medium text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{template.nome}</h4>
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                {template.categoria || 'MARKETING'}
                              </span>
                              {getActiveInstancias(template).map((ti, idx) => (
                                <span key={idx} className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate max-w-[120px] ${
                                  ti.status === 'aprovado' ? (darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                                  : ti.status === 'rejeitado' ? (darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                                  : (darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700')
                                }`} title={`${ti.instancia?.nome_instancia || 'Instância'}: ${ti.status}`}>
                                  {ti.status === 'aprovado' ? '✅' : ti.status === 'rejeitado' ? '❌' : '⏳'} {ti.instancia?.nome_instancia?.split(' ')[0] || '?'}
                                </span>
                              ))}
                            </div>
                            {template.rejection_reason && (
                              <div className={`mt-2 p-1.5 rounded text-[10px] ${darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
                                ❌ {template.rejection_reason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }

          // === GRID ===
          if (viewMode === 'grid') {
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map(template => {
                  const badge = getStatusBadge(template._statusFiltrado);
                  const StatusIcon = badge.icon;
                  return (
                    <div key={template.id} onClick={() => setSelectedTemplate(template)}
                      className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${darkMode ? 'bg-zinc-800 border-gray-700 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-gray-400'}`}>
                      <div className={`rounded-lg p-2 mb-3 ${darkMode ? 'bg-zinc-900' : 'bg-[#e5ddd5]'}`}>
                        <div className="bg-[#DCF8C6] text-gray-900 rounded-lg shadow-sm overflow-hidden max-w-[90%]">
                          {template.media_url && template.media_type === 'IMAGE' && (
                            <img src={template.media_url} alt={template.nome} className="w-full h-28 object-cover" />
                          )}
                          {template.media_type && template.media_type !== 'IMAGE' && (
                            <div className="flex items-center justify-center h-16 bg-[#c8e6c9]">
                              {template.media_type === 'VIDEO' ? <Video className="w-8 h-8 text-gray-600" /> : <File className="w-8 h-8 text-gray-600" />}
                            </div>
                          )}
                          <div className="p-1.5">
                            <p className="text-xs line-clamp-2 break-words">{template.mensagem}</p>
                            <div className="text-[9px] text-gray-500 text-right mt-0.5">{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </div>
                      </div>
                      <h3 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{template.nome}</h3>
                      <div className="flex items-center flex-wrap gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white ${badge.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />{badge.text}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                          {template.categoria || 'MARKETING'}
                        </span>
                        {getActiveInstancias(template).map((ti, idx) => (
                          <span key={idx} className={`px-2 py-1 rounded text-xs font-medium ${
                            ti.status === 'aprovado' ? (darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                            : ti.status === 'rejeitado' ? (darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                            : (darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700')
                          }`} title={`${ti.instancia?.nome_instancia}: ${ti.status}`}>
                            {ti.status === 'aprovado' ? '✅' : ti.status === 'rejeitado' ? '❌' : '⏳'} {ti.instancia?.nome_instancia}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }

          // === TABELA ===
          return (
            <div className="overflow-x-auto">
            <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className={darkMode ? 'bg-zinc-800' : 'bg-gray-50'}>
                    {['Nome', 'Status', 'Categoria', 'Instância', 'Mídia', 'Criado em', 'Ações'].map(h => (
                      <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {filtered.map(template => {
                    const badge = getStatusBadge(template._statusFiltrado);
                    const StatusIcon = badge.icon;
                    return (
                      <tr key={template.id} onClick={() => setSelectedTemplate(template)}
                        className={`cursor-pointer transition-colors ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'}`}>
                        <td className={`px-4 py-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          <div className="font-medium text-sm">{template.nome}</div>
                          <div className={`text-xs line-clamp-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{template.mensagem}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white ${badge.color}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />{badge.text}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                            {template.categoria || 'MARKETING'}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <div className="flex flex-wrap gap-1.5 min-w-[180px]">
                            {getActiveInstancias(template).map((ti, idx) => (
                              <span key={idx} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                ti.status === 'aprovado' ? (darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                                : ti.status === 'rejeitado' ? (darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                                : (darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700')
                              }`} title={`${ti.instancia?.nome_instancia}: ${ti.status}`}>
                                {ti.status === 'aprovado' ? '✅' : ti.status === 'rejeitado' ? '❌' : '⏳'} {ti.instancia?.nome_instancia || '—'}
                              </span>
                            ))}
                            {getActiveInstancias(template).length === 0 && <span>—</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {template.media_type ? (
                            <span className={`inline-flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {template.media_type === 'IMAGE' ? <Image className="w-3.5 h-3.5" /> : template.media_type === 'VIDEO' ? <Video className="w-3.5 h-3.5" /> : <File className="w-3.5 h-3.5" />}
                              {template.media_type}
                            </span>
                          ) : <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>—</span>}
                        </td>
                        <td className={`px-4 py-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {new Date(template.create_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            {['rascunho', 'rejeitado'].includes(template.status) && (
                              <>
                                <button onClick={() => {
                                  setEditingTemplate(template);
                                  setFormData({ nome: template.nome, mensagem: template.mensagem, categoria: template.categoria, instancia_id: template.instancia_id || '', media_type: template.media_type || '', media_url: template.media_url || '', buttons: template.buttons || [] });
                                  setMediaFile(null); setShowModal(true);
                                }} className={`p-1.5 rounded ${darkMode ? 'hover:bg-zinc-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`} title="Editar">
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleSubmeter(template.id)} className="p-1.5 rounded hover:bg-blue-100 text-blue-500" title="Submeter">
                                  <Send className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                            {template.status === 'submetido' && (
                              <button onClick={() => handleVerificarStatus(template.id)} className="p-1.5 rounded hover:bg-yellow-100 text-yellow-500" title="Verificar">
                                <Clock className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => handleDelete(template.id)} className="p-1.5 rounded hover:bg-red-100 text-red-500" title="Excluir">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </div>
          );
        })()}

        {/* Modal de Detalhes */}
        {selectedTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTemplate(null)}>
            <div onClick={(e) => e.stopPropagation()} className={`rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedTemplate.nome}</h3>
                  <button onClick={() => setSelectedTemplate(null)} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex items-center flex-wrap gap-2 mb-6">
                  {(() => { const b = getStatusBadge(selectedTemplate.status); const I = b.icon; return (
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium text-white ${b.color}`}><I className="h-3 w-3 mr-1" />{b.text}</span>
                  );})()}
                  <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                    {selectedTemplate.categoria || 'MARKETING'}
                  </span>
                  {selectedTemplate.instancia && !selectedTemplate.instancias?.length && (
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                      {selectedTemplate.instancia.nome_instancia}
                    </span>
                  )}
                </div>

                <div className="mb-6">
                  <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>📱 Preview WhatsApp</h4>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-[#e5ddd5]'}`}>
                    <div className="relative max-w-[85%]">
                      <div className="bg-[#DCF8C6] text-gray-900 rounded-lg shadow-sm relative overflow-hidden">
                        {selectedTemplate.media_url && selectedTemplate.media_type === 'IMAGE' && (
                          <img src={selectedTemplate.media_url} alt="Mídia" className="w-full max-h-60 object-cover" />
                        )}
                        {selectedTemplate.media_url && selectedTemplate.media_type === 'VIDEO' && (
                          <video src={selectedTemplate.media_url} controls className="w-full max-h-60" />
                        )}
                        {selectedTemplate.media_url && selectedTemplate.media_type === 'DOCUMENT' && (
                          <a href={selectedTemplate.media_url} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 p-3 bg-[#c8e6c9]">
                            <File className="w-5 h-5 text-gray-700" />
                            <span className="text-sm text-gray-700">Ver documento</span>
                          </a>
                        )}
                        <div className="p-2">
                          <div className="text-sm whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word' }}>
                            {selectedTemplate.mensagem}
                          </div>
                          <div className="text-[10px] text-gray-600 text-right mt-1">
                            {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        {selectedTemplate.buttons?.length > 0 && (
                          <div className="border-t border-gray-300/50">
                            {selectedTemplate.buttons.map((btn, i) => (
                              <div key={i} className="text-center py-2 text-[#0096DE] text-sm font-medium border-b border-gray-300/30 last:border-b-0">
                                {btn.type === 'URL' && '🔗 '}
                                {btn.type === 'PHONE_NUMBER' && '📞 '}
                                {btn.text}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="absolute bottom-0 right-[-8px] w-0 h-0 border-l-[8px] border-l-[#DCF8C6] border-b-[8px] border-b-transparent"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedTemplate.rejection_reason && (
                  <div className={`p-4 rounded-lg mb-6 ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>❌ Motivo da rejeição</p>
                    <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-500'}`}>{selectedTemplate.rejection_reason}</p>
                  </div>
                )}

                <div className="mb-6">
                  <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Informações</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Criado em</p>
                      <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{new Date(selectedTemplate.create_at).toLocaleString('pt-BR')}</p>
                    </div>
                    {selectedTemplate.submetido_em && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Submetido em</p>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{new Date(selectedTemplate.submetido_em).toLocaleString('pt-BR')}</p>
                      </div>
                    )}
                    {selectedTemplate.aprovado_em && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Aprovado em</p>
                        <p className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{new Date(selectedTemplate.aprovado_em).toLocaleString('pt-BR')}</p>
                      </div>
                    )}
                    {selectedTemplate.template_meta_id && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>ID Meta</p>
                        <p className={`text-sm font-mono ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{selectedTemplate.template_meta_id}</p>
                      </div>
                    )}
                    {selectedTemplate.variaveis?.length > 0 && (
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Variáveis</p>
                        <p className={`text-sm font-mono ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{selectedTemplate.variaveis.map(v => `{{${v}}}`).join(', ')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedTemplate.campanhas?.length > 0 && (
                  <div className="mb-6">
                    <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Campanhas usando este template</h4>
                    <div className="space-y-2">
                      {selectedTemplate.campanhas.map(c => (
                        <div key={c.id} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.nome}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            c.status_execucao === 'completa' ? 'bg-green-500/20 text-green-400' :
                            c.status_execucao === 'executando' ? 'bg-blue-500/20 text-blue-400' :
                            darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                          }`}>{c.status_execucao || 'rascunho'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Instâncias onde este template está aprovado/submetido */}
                {(selectedTemplate.instancias?.filter(ti => activeInstanceIds.has(ti.instancia_id || ti.instancia?.id)).length > 0) && (
                  <div className="mb-6">
                    <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>📡 Instâncias</h4>
                    <div className="space-y-2">
                      {selectedTemplate.instancias.filter(ti => activeInstanceIds.has(ti.instancia_id || ti.instancia?.id)).map((ti, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{ti.status === 'aprovado' ? '✅' : ti.status === 'rejeitado' ? '❌' : '⏳'}</span>
                            <div>
                              <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ti.instancia?.nome_instancia || 'Instância'}</p>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{ti.instancia?.numero_whatsapp || ''}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            ti.status === 'aprovado' ? (darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700') :
                            ti.status === 'rejeitado' ? (darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700') :
                            (darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                          }`}>{ti.status}</span>
                        </div>
                      ))}
                    </div>
                    {instances.filter(i => !selectedTemplate.instancias.some(ti => ti.instancia_id === i.id)).length > 0 && (
                      <button
                        onClick={() => { setSelectedInstancias([]); setShowSubmitModal(selectedTemplate.id); }}
                        className={`mt-3 w-full py-2 rounded-lg text-sm font-medium border border-dashed transition ${
                          darkMode ? 'border-zinc-600 text-gray-400 hover:border-zinc-400 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700'
                        }`}
                      >
                        + Aprovar em outra instância
                      </button>
                    )}
                  </div>
                )}

                <div className={`flex items-center gap-3 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  {['rascunho', 'rejeitado'].includes(selectedTemplate.status) && (
                    <>
                      <button
                        onClick={() => {
                          setEditingTemplate(selectedTemplate);
                          setFormData({ 
                            nome: selectedTemplate.nome, 
                            mensagem: selectedTemplate.mensagem, 
                            categoria: selectedTemplate.categoria, 
                            instancia_id: selectedTemplate.instancia_id || '',
                            media_type: selectedTemplate.media_type || '',
                            media_url: selectedTemplate.media_url || ''
                          });
                          setMediaFile(null);
                          setSelectedTemplate(null);
                          setShowModal(true);
                        }}
                        className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
                      >
                        <Edit className="h-4 w-4 mr-1.5" />Editar
                      </button>
                      <button
                        onClick={() => { const id = selectedTemplate.id; setSelectedTemplate(null); handleSubmeter(id); }}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                      >
                        <Send className="h-4 w-4 mr-1.5" />{selectedTemplate.status === 'rejeitado' ? 'Resubmeter' : 'Submeter'}
                      </button>
                    </>
                  )}
                  {selectedTemplate.status === 'submetido' && (
                    <button
                      onClick={async () => { 
                        await handleVerificarStatus(selectedTemplate.id); 
                        const res = await templatesMetaAPI.list();
                        const updated = (res.data || []).find(t => t.id === selectedTemplate.id);
                        if (updated) setSelectedTemplate(updated);
                        loadTemplates();
                      }}
                      className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium"
                    >
                      <Clock className="h-4 w-4 mr-1.5" />Verificar Status
                    </button>
                  )}
                  {selectedTemplate.status === 'aprovado' && (
                    <span className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>✅ Pronto para uso em campanhas</span>
                  )}
                  <button
                    onClick={() => { const id = selectedTemplate.id; setSelectedTemplate(null); handleDelete(id); }}
                    className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium ml-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />Excluir
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg shadow-xl max-w-2xl w-full ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {editingTemplate ? 'Editar Template' : 'Novo Template'}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Nome do Template
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className={`w-full rounded-lg px-4 py-2 ${
                        darkMode
                          ? 'bg-zinc-700 text-white border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                      } border focus:ring-2 focus:ring-nomoz-orange`}
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mensagem
                    </label>
                    <textarea
                      value={formData.mensagem}
                      onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                      rows={6}
                      className={`w-full rounded-lg px-4 py-2 ${
                        darkMode
                          ? 'bg-zinc-700 text-white border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                      } border focus:ring-2 focus:ring-nomoz-orange`}
                      placeholder="Use {{nome}}, {{telefone}} para variáveis"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Categoria
                    </label>
                    <select
                      value={formData.categoria}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                      className={`w-full rounded-lg px-4 py-2 ${
                        darkMode
                          ? 'bg-zinc-700 text-white border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                      } border focus:ring-2 focus:ring-nomoz-orange`}
                    >
                      <option value="MARKETING">Marketing</option>
                      <option value="UTILITY">Utilidade</option>
                      <option value="AUTHENTICATION">Autenticação</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Instância
                    </label>
                    <select
                      value={formData.instancia_id}
                      onChange={(e) => setFormData({ ...formData, instancia_id: e.target.value })}
                      className={`w-full rounded-lg px-4 py-2 ${
                        darkMode
                          ? 'bg-zinc-700 text-white border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                      } border focus:ring-2 focus:ring-nomoz-orange`}
                    >
                      <option value="">Selecione uma instância</option>
                      {instances.map((instance) => (
                        <option key={instance.id} value={instance.id}>
                          {instance.nome_instancia} • {instance.platform}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mídia (Opcional)
                    </label>
                    <select
                      value={formData.media_type}
                      onChange={(e) => setFormData({ ...formData, media_type: e.target.value })}
                      className={`w-full rounded-lg px-4 py-2 mb-2 ${
                        darkMode
                          ? 'bg-zinc-700 text-white border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                      } border focus:ring-2 focus:ring-nomoz-orange`}
                    >
                      <option value="">Sem mídia</option>
                      <option value="IMAGE">Imagem</option>
                      <option value="VIDEO">Vídeo</option>
                      <option value="DOCUMENT">Documento</option>
                    </select>

                    {formData.media_type && (
                      <div className={`border-2 border-dashed rounded-lg p-4 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                        {formData.media_url || mediaFile ? (
                          <div className="space-y-2">
                            {formData.media_type === 'IMAGE' && formData.media_url && (
                              <img src={formData.media_url} alt="Preview" className="max-h-40 rounded" />
                            )}
                            <div className="flex items-center justify-between">
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {mediaFile?.name || 'Mídia carregada'}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setMediaFile(null);
                                  setFormData({ ...formData, media_url: '' });
                                }}
                                className="text-red-500 hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="cursor-pointer flex flex-col items-center">
                            <Upload className={`w-8 h-8 mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Clique para fazer upload
                            </span>
                            <input
                              type="file"
                              accept={
                                formData.media_type === 'IMAGE' ? 'image/*' :
                                formData.media_type === 'VIDEO' ? 'video/*' :
                                '.pdf,.doc,.docx'
                              }
                              onChange={(e) => setMediaFile(e.target.files[0])}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Botões */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Botões (Opcional - máx. 3)
                    </label>
                    {formData.buttons.map((btn, index) => (
                      <div key={index} className={`flex items-center gap-2 mb-2 p-2 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                        <select
                          value={btn.type}
                          onChange={(e) => {
                            const updated = [...formData.buttons];
                            updated[index] = { ...updated[index], type: e.target.value, url: '', phone_number: '' };
                            setFormData({ ...formData, buttons: updated });
                          }}
                          className={`rounded px-2 py-1 text-sm ${darkMode ? 'bg-zinc-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'} border`}
                        >
                          <option value="QUICK_REPLY">Resposta Rápida</option>
                          <option value="URL">Link (URL)</option>
                          <option value="PHONE_NUMBER">Telefone</option>
                        </select>
                        <input
                          type="text"
                          value={btn.text}
                          onChange={(e) => {
                            const updated = [...formData.buttons];
                            updated[index] = { ...updated[index], text: e.target.value };
                            setFormData({ ...formData, buttons: updated });
                          }}
                          placeholder="Texto do botão"
                          className={`flex-1 rounded px-2 py-1 text-sm ${darkMode ? 'bg-zinc-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'} border`}
                          maxLength={25}
                        />
                        {btn.type === 'URL' && (
                          <input
                            type="text"
                            value={btn.url || ''}
                            onChange={(e) => {
                              const updated = [...formData.buttons];
                              updated[index] = { ...updated[index], url: e.target.value };
                              setFormData({ ...formData, buttons: updated });
                            }}
                            placeholder="https://..."
                            className={`flex-1 rounded px-2 py-1 text-sm ${darkMode ? 'bg-zinc-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'} border`}
                          />
                        )}
                        {btn.type === 'PHONE_NUMBER' && (
                          <input
                            type="text"
                            value={btn.phone_number || ''}
                            onChange={(e) => {
                              const updated = [...formData.buttons];
                              updated[index] = { ...updated[index], phone_number: e.target.value };
                              setFormData({ ...formData, buttons: updated });
                            }}
                            placeholder="+5519999999999"
                            className={`flex-1 rounded px-2 py-1 text-sm ${darkMode ? 'bg-zinc-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'} border`}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formData.buttons.filter((_, i) => i !== index);
                            setFormData({ ...formData, buttons: updated });
                          }}
                          className="text-red-500 hover:text-red-600 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {formData.buttons.length < 3 && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, buttons: [...formData.buttons, { type: 'QUICK_REPLY', text: '' }] })}
                        className={`w-full py-2 border-2 border-dashed rounded-lg text-sm ${darkMode ? 'border-gray-600 text-gray-400 hover:border-gray-500' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}
                      >
                        + Adicionar Botão
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className={`flex items-center justify-end space-x-3 px-6 py-4 border-t ${
                darkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className={`px-4 py-2 rounded-lg ${
                    darkMode
                      ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploadingMedia}
                  className="px-4 py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg disabled:opacity-50"
                >
                  {uploadingMedia ? 'Enviando mídia...' : editingTemplate ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Copiar Templates para outra instância */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Copiar Templates entre Instâncias
              </h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Selecione quais templates deseja copiar para outra(s) instância(s).
              </p>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Instância Origem</label>
                  <select
                    value={copyOrigem}
                    onChange={(e) => {
                      setCopyOrigem(e.target.value);
                      setCopyTemplateIds([]);
                    }}
                    className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    <option value="">Selecione...</option>
                    {instances.map(inst => (
                      <option key={inst.id} value={inst.id}>{inst.nome_instancia} ({inst.numero_whatsapp})</option>
                    ))}
                  </select>
                </div>

                {/* Seleção de Templates */}
                {copyOrigem && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Templates</label>
                      <button
                        onClick={() => {
                          const origemTemplates = templates.filter(t => {
                            if (t.instancia_id === copyOrigem && t.status === 'aprovado') return true;
                            return t.instancias?.some(ti => (ti.instancia_id || ti.instancia?.id) === copyOrigem && ti.status === 'aprovado');
                          });
                          if (copyTemplateIds.length === origemTemplates.length) setCopyTemplateIds([]);
                          else setCopyTemplateIds(origemTemplates.map(t => t.id));
                        }}
                        className={`text-xs ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                      >
                        {copyTemplateIds.length === templates.filter(t => {
                          if (t.instancia_id === copyOrigem && t.status === 'aprovado') return true;
                          return t.instancias?.some(ti => (ti.instancia_id || ti.instancia?.id) === copyOrigem && ti.status === 'aprovado');
                        }).length ? 'Desmarcar todos' : 'Selecionar todos'}
                      </button>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {templates.filter(t => {
                        if (t.instancia_id === copyOrigem && t.status === 'aprovado') return true;
                        return t.instancias?.some(ti => (ti.instancia_id || ti.instancia?.id) === copyOrigem && ti.status === 'aprovado');
                      }).length === 0 ? (
                        <p className={`text-sm py-4 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhum template aprovado nesta instância</p>
                      ) : templates.filter(t => {
                        if (t.instancia_id === copyOrigem && t.status === 'aprovado') return true;
                        return t.instancias?.some(ti => (ti.instancia_id || ti.instancia?.id) === copyOrigem && ti.status === 'aprovado');
                      }).map(t => (
                        <label key={t.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                          copyTemplateIds.includes(t.id)
                            ? (darkMode ? 'bg-nomoz-orange/10 border border-nomoz-orange/50' : 'bg-orange-50 border border-orange-300')
                            : (darkMode ? 'bg-zinc-700 border border-zinc-600 hover:border-zinc-500' : 'bg-gray-50 border border-gray-200 hover:border-gray-300')
                        }`}>
                          <input
                            type="checkbox"
                            checked={copyTemplateIds.includes(t.id)}
                            onChange={(e) => {
                              if (e.target.checked) setCopyTemplateIds(prev => [...prev, t.id]);
                              else setCopyTemplateIds(prev => prev.filter(id => id !== t.id));
                            }}
                            className="w-4 h-4 rounded text-nomoz-orange focus:ring-nomoz-orange"
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t.nome}</p>
                            <p className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t.categoria} • {t.mensagem?.substring(0, 50)}...</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Instâncias Destino</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {instances.filter(i => i.id !== copyOrigem).map(inst => (
                      <label key={inst.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${
                        copyDestinos.includes(inst.id)
                          ? (darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-300')
                          : (darkMode ? 'bg-zinc-700 border border-zinc-600' : 'bg-gray-50 border border-gray-200')
                      }`}>
                        <input
                          type="checkbox"
                          checked={copyDestinos.includes(inst.id)}
                          onChange={(e) => {
                            if (e.target.checked) setCopyDestinos(prev => [...prev, inst.id]);
                            else setCopyDestinos(prev => prev.filter(id => id !== inst.id));
                          }}
                          className="w-4 h-4 rounded text-nomoz-orange focus:ring-nomoz-orange"
                        />
                        <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{inst.nome_instancia}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => { setShowCopyModal(false); setCopyOrigem(''); setCopyDestinos([]); setCopyTemplateIds([]); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (!copyOrigem || !copyDestinos.length || !copyTemplateIds.length) return toast.error('Selecione origem, templates e destino');
                    setCopying(true);
                    try {
                      const res = await templatesMetaAPI.copiarTemplates(copyOrigem, copyDestinos, copyTemplateIds);
                      const { total, submetidos, pulados, erros } = res.data;
                      toast.success(`${submetidos} template(s) submetido(s) de ${total} selecionado(s) (${pulados} já existiam)`);
                      if (erros?.length) toast.warn(`${erros.length} erro(s)`);
                      setShowCopyModal(false);
                      setCopyOrigem('');
                      setCopyDestinos([]);
                      setCopyTemplateIds([]);
                      loadTemplates();
                    } catch (e) {
                      toast.error(e.response?.data?.message || 'Erro ao copiar templates');
                    } finally {
                      setCopying(false);
                    }
                  }}
                  disabled={copying || !copyOrigem || !copyDestinos.length || !copyTemplateIds.length}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-nomoz-orange hover:bg-nomoz-orange-dark text-white disabled:opacity-50"
                >
                  {copying ? 'Copiando...' : `Copiar ${copyTemplateIds.length} Template(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Submeter em Múltiplas Instâncias */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl max-w-md w-full ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Submeter Template
              </h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Selecione em quais instâncias deseja aprovar este template:
              </p>

              <div className="space-y-2 max-h-60 overflow-y-auto">
                {instances.filter(inst => {
                  // Esconder instâncias que já têm esse template (aprovado ou submetido)
                  const template = templates.find(t => t.id === showSubmitModal);
                  const jaAssociada = template?.instancias?.some(ti => (ti.instancia_id || ti.instancia?.id) === inst.id);
                  return !jaAssociada;
                }).map(inst => (
                  <label key={inst.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                    selectedInstancias.includes(inst.id)
                      ? (darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-300')
                      : (darkMode ? 'bg-zinc-700 border border-zinc-600 hover:border-zinc-500' : 'bg-gray-50 border border-gray-200 hover:border-gray-300')
                  }`}>
                    <input
                      type="checkbox"
                      checked={selectedInstancias.includes(inst.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedInstancias(prev => [...prev, inst.id]);
                        else setSelectedInstancias(prev => prev.filter(id => id !== inst.id));
                      }}
                      className="w-4 h-4 rounded text-nomoz-orange focus:ring-nomoz-orange"
                    />
                    <div className="flex-1">
                      <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{inst.nome_instancia}</p>
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{inst.numero_whatsapp || inst.phone_number_id}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowSubmitModal(null)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmeterMulti}
                  disabled={submitting || !selectedInstancias.length}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-nomoz-orange hover:bg-nomoz-orange-dark text-white disabled:opacity-50"
                >
                  {submitting ? 'Submetendo...' : `Submeter em ${selectedInstancias.length} instância(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Templates;
